<template>
  <slot />
</template>

<script setup lang="ts">
import { computed, onMounted, provide } from 'vue';
import { watchArray } from '@vueuse/core';
import {
  type UseTrackingComposable,
  trackingInjectKey, type EventName,
} from '@/components/tracking/useTracking';
import { useCheckoutStore } from '@/stores/checkout';
import { Checkout } from '@vp/common';

declare var window: {
  dataLayer: any[];
};

type Platform = 'google' | 'facebook';

const eventMap: Partial<{ [k in Platform]: Partial<{ [k in EventName]: string }> }> = {
  facebook: {
    view_item: 'ViewContent',
    add_to_cart: 'AddToCart',
    begin_checkout: 'InitiateCheckout',
    purchase: 'Purchase',
    add_payment_info: 'AddPaymentInfo',
  },
};

onMounted(() => {
  const script = document.createElement('script');
  script.setAttribute('src', 'https://www.googletagmanager.com/gtag/js');
  document.head.appendChild(script);
});

const checkoutStore = useCheckoutStore();
const trackingEntries = computed<Checkout.TrackingEntry.TrackingEntry[]>(
  () => checkoutStore.getEventCheckoutConfig?.tracking || [],
);

function gtag() {
  if (!import.meta.env.SSR) {
    window.dataLayer.push(arguments);
  }
}

if (!import.meta.env.SSR) {
  window.dataLayer = window.dataLayer || [];
  gtag('js', new Date());

  // eslint-disable
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      n.callMethod
        ? n.callMethod.apply(n, arguments)
        : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  }) (window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  // eslint-enable

  watchArray(trackingEntries, (_new, _old, added, _removed) => {
    added.forEach(entry => {
      switch (entry.provider) {
        case 'ga':
          gtag('config', entry.trackingId);
          break;
        case 'facebook':
          window.fbq('init', entry.trackingId);
          window.fbq('track', 'PageView');
          break;
      }
    });
  }, { immediate: true });
}

const fbTrack = (...args: any[]) => {
  if (import.meta.env.SSR) {
    return;
  }

  try {
    window.fbq(...args);
  } catch {
    // NOP
  }
};

const track = (...args: any[]) => {
  gtag(...args);
};

const trackEvent = (event: EventName, data: unknown) => {
  try {
    gtag('event', event, data);
  } catch {
    // NOP
  }

  const fbEvent = eventMap.facebook?.[event] || event;

  fbTrack('track', fbEvent, data);
};

provide<UseTrackingComposable>(trackingInjectKey, {
  track,
  trackEvent,
});
</script>
